import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ChatBox from "../components/ChatBox";
import StreamViewingMenu from "../components/StreamViewingMenu";
import { apiUrl } from "../lib/apiBase";
import { getAuthHeader } from "../lib/auth";
import "./Stream.css";

const DEFAULT_TOURNAMENT_SLUG = "get-active-3v3-75-ovr";
const DEFAULT_TOURNAMENT_ID = "get-active-3v3-75-ovr-2026";
const CREW_HQ_SLUG = "gas-up-3v3-crew-headquarters";
const CREW_HQ_ID = "gas-up-3v3-crew-hq-2026";
const TWITCH_SCRIPT = "https://player.twitch.tv/js/embed/v1.js";

const TOURNAMENT_ID_BY_SLUG = {
  [DEFAULT_TOURNAMENT_SLUG]: DEFAULT_TOURNAMENT_ID,
  [DEFAULT_TOURNAMENT_ID]: DEFAULT_TOURNAMENT_ID,
  [CREW_HQ_SLUG]: CREW_HQ_ID,
  [CREW_HQ_ID]: CREW_HQ_ID,
};

function tournamentIdFromSlug(slug) {
  const value = String(slug || DEFAULT_TOURNAMENT_SLUG).trim().toLowerCase();
  return TOURNAMENT_ID_BY_SLUG[value] || value;
}

function signedIn() {
  return Boolean(getAuthHeader());
}

async function fetchJson(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    method: options.method || "GET",
    credentials: "omit",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.auth && getAuthHeader() ? { Authorization: getAuthHeader() } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

function tournamentTwitchQualityHeight(value) {
  const match = String(value || "").match(/(\d{3,4})p/i);
  return match ? Number(match[1]) : 0;
}

function tournamentTwitchQualityOption(value, lowestNumeric) {
  const quality = String(value || "").trim();
  const lower = quality.toLowerCase();
  if (lower === "auto") {
    return {
      value: quality,
      label: "Auto",
      shortLabel: "Auto",
      detail: "Twitch adapts quality to the viewer connection.",
    };
  }
  if (lower === "chunked") {
    return {
      value: quality,
      label: "Source",
      shortLabel: "Source",
      detail: "Original broadcaster quality and highest data use.",
    };
  }
  const height = tournamentTwitchQualityHeight(quality);
  const isLowest = height > 0 && height === lowestNumeric;
  return {
    value: quality,
    label: isLowest ? `Data Saver • ${quality}` : quality,
    shortLabel: isLowest ? "Data Saver" : quality,
    detail: isLowest
      ? "Lowest Twitch rendition available for this tournament feed."
      : "Fixed Twitch playback quality.",
  };
}

function loadTwitchPlayerScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("browser_required"));
  if (window.Twitch?.Player) return Promise.resolve(window.Twitch);

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TWITCH_SCRIPT}"]`);
    if (existing) {
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (window.Twitch?.Player) {
          window.clearInterval(timer);
          resolve(window.Twitch);
        } else if (Date.now() - started > 10000) {
          window.clearInterval(timer);
          reject(new Error("twitch_player_timeout"));
        }
      }, 100);
      return;
    }

    const script = document.createElement("script");
    script.src = TWITCH_SCRIPT;
    script.async = true;
    script.onload = () =>
      window.Twitch?.Player
        ? resolve(window.Twitch)
        : reject(new Error("twitch_player_missing"));
    script.onerror = () => reject(new Error("twitch_player_load_failed"));
    document.head.appendChild(script);
  });
}

function TwitchHubPlayer({ source, onStatus }) {
  const hostId = useMemo(
    () =>
      `ga-tourney-twitch-${String(source?.id || "feed").replace(/[^a-z0-9_-]/gi, "-")}-${Math.random()
        .toString(36)
        .slice(2)}`,
    [source?.id]
  );
  const playerRef = useRef(null);
  const [qualityOptions, setQualityOptions] = useState([
    { value: "auto", label: "Auto", shortLabel: "Auto", detail: "Twitch adapts quality to the viewer connection." },
  ]);
  const [quality, setQuality] = useState("auto");
  const [viewingStatus, setViewingStatus] = useState("");

  useEffect(() => {
    let cancelled = false;
    let player = null;

    async function boot() {
      try {
        const Twitch = await loadTwitchPlayerScript();
        if (cancelled) return;
        const parents = Array.from(
          new Set(
            [
              window.location.hostname,
              "gastreaming.live",
              "www.gastreaming.live",
              "stream-site-rho.vercel.app",
            ].filter(Boolean)
          )
        );

        player = new Twitch.Player(hostId, {
          channel: source.twitchChannel,
          width: "100%",
          height: "100%",
          autoplay: true,
          muted: true,
          parent: parents,
        });
        playerRef.current = player;

        const refreshViewingOptions = () => {
          if (cancelled || playerRef.current !== player) return;
          try {
            const rawQualities = Array.from(
              new Set((player.getQualities?.() || []).map((value) => String(value || "").trim()).filter(Boolean))
            );
            const numeric = rawQualities
              .map(tournamentTwitchQualityHeight)
              .filter((value) => value > 0)
              .sort((a, b) => a - b);
            const lowestNumeric = numeric[0] || 0;
            const ordered = rawQualities.sort((a, b) => {
              const al = a.toLowerCase();
              const bl = b.toLowerCase();
              if (al === "auto") return -1;
              if (bl === "auto") return 1;
              if (al === "chunked") return 1;
              if (bl === "chunked") return -1;
              return tournamentTwitchQualityHeight(a) - tournamentTwitchQualityHeight(b);
            });
            if (ordered.length) {
              setQualityOptions(
                ordered.map((item) => tournamentTwitchQualityOption(item, lowestNumeric))
              );
            }
            setQuality(String(player.getQuality?.() || "auto").trim() || "auto");
          } catch {
            // Quality data may not be ready until playback begins.
          }

          const iframe = document.getElementById(hostId)?.querySelector?.("iframe");
          if (iframe) {
            const permissions = new Set(
              String(iframe.getAttribute("allow") || "")
                .split(";")
                .map((item) => item.trim())
                .filter(Boolean)
            );
            permissions.add("autoplay");
            permissions.add("fullscreen");
            permissions.add("picture-in-picture");
            iframe.setAttribute("allow", Array.from(permissions).join("; "));
            iframe.setAttribute("allowfullscreen", "true");
          }
        };

        const online = () => !cancelled && onStatus?.("online");
        const offline = () => !cancelled && onStatus?.("offline");
        player.addEventListener(Twitch.Player.READY, refreshViewingOptions);
        player.addEventListener(Twitch.Player.ONLINE, online);
        player.addEventListener(Twitch.Player.PLAYING, () => {
          online();
          refreshViewingOptions();
        });
        player.addEventListener(Twitch.Player.OFFLINE, offline);
        player.addEventListener(Twitch.Player.ENDED, offline);
      } catch (error) {
        console.warn("[TOURNAMENT LIVE HUB] Twitch player failed", error?.message || error);
        if (!cancelled) onStatus?.("error");
      }
    }

    onStatus?.("checking");
    boot();
    return () => {
      cancelled = true;
      try {
        playerRef.current?.pause?.();
      } catch {
        // Twitch player cleanup is best effort.
      }
      playerRef.current = null;
      const host = document.getElementById(hostId);
      if (host) host.innerHTML = "";
    };
  }, [hostId, onStatus, source.twitchChannel]);

  const changeQuality = useCallback((nextQuality) => {
    const player = playerRef.current;
    if (!player || !nextQuality) return;
    try {
      player.setQuality(nextQuality);
      setQuality(nextQuality);
      setViewingStatus(
        String(nextQuality).toLowerCase() === "auto"
          ? "Automatic Twitch quality is on."
          : `Tournament feed quality set to ${nextQuality}.`
      );
    } catch {
      setViewingStatus("That Twitch quality is not available right now.");
    }
  }, []);

  const enableTwitchPip = useCallback(async () => {
    const iframe = document.getElementById(hostId)?.querySelector?.("iframe");
    if (iframe) {
      const permissions = new Set(
        String(iframe.getAttribute("allow") || "")
          .split(";")
          .map((item) => item.trim())
          .filter(Boolean)
      );
      permissions.add("picture-in-picture");
      permissions.add("fullscreen");
      iframe.setAttribute("allow", Array.from(permissions).join("; "));
    }
    setViewingStatus(
      "Picture-in-Picture permission is enabled for this Twitch feed. Use the Twitch player’s PiP control when your device/browser exposes it."
    );
    return true;
  }, [hostId]);

  return (
    <>
      <div id={hostId} className="ga-th-twitch-host" />
      <StreamViewingMenu
        qualityOptions={qualityOptions}
        qualityValue={quality}
        onQualityChange={changeQuality}
        onPictureInPicture={enableTwitchPip}
        pictureInPictureAvailable={true}
        sourceLabel="Tournament console feed • Twitch"
        statusText={
          viewingStatus ||
          "Use Data Saver for slow mobile data. Twitch supplies the available renditions for each participant broadcast."
        }
      />
    </>
  );
}

function matchComplete(match) {
  return ["complete", "completed", "finished"].includes(
    String(match?.status || "").toLowerCase()
  );
}

function currentMatchForTeam(matches, teamId) {
  const needle = String(teamId || "");
  return (
    (Array.isArray(matches) ? matches : []).find(
      (match) =>
        match?.teamA &&
        match?.teamB &&
        !matchComplete(match) &&
        (String(match?.teamA?.id || "") === needle ||
          String(match?.teamB?.id || "") === needle)
    ) || null
  );
}

function matchLabel(match, fallback = "Tournament Game") {
  if (match?.teamA?.name && match?.teamB?.name) {
    return `${match.teamA.name} vs ${match.teamB.name}`;
  }
  return fallback;
}

function roundLabel(match) {
  if (!match) return "TOURNAMENT MATCH";
  if (
    String(match?.id || "").toLowerCase() === "championship" ||
    String(match?.conference || "").toLowerCase() === "final"
  ) {
    return "CHAMPIONSHIP";
  }
  const conference = String(match?.conference || "").toUpperCase();
  const round = match?.round ? `ROUND ${match.round}` : "MATCH";
  return [conference, round].filter(Boolean).join(" • ");
}

function sourceStatus(source, statuses) {
  const status = statuses[source?.id] || "attached";
  if (status === "online") return "LIVE";
  if (status === "checking") return "CHECKING";
  if (status === "offline") return "OFFLINE";
  return "ATTACHED";
}

export default function TournamentLiveHub() {
  const params = useParams();
  const tournamentSlug = String(params.tournamentSlug || DEFAULT_TOURNAMENT_SLUG).trim();
  const tournamentId = tournamentIdFromSlug(tournamentSlug);
  const userSignedIn = signedIn();

  const [tournament, setTournament] = useState(null);
  const [sources, setSources] = useState([]);
  const [mySetup, setMySetup] = useState(null);
  const [selectedId, setSelectedId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("feed") || "";
    } catch {
      return "";
    }
  });
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupNotice, setSetupNotice] = useState("");
  const [twitchChannel, setTwitchChannel] = useState("");
  const [platform, setPlatform] = useState("ps5");

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
        setError("");
      }

      const requests = [
        fetchJson(`/tournaments/${encodeURIComponent(tournamentId)}`),
        fetchJson(`/connect/tournament-streams/${encodeURIComponent(tournamentId)}`),
      ];
      if (userSignedIn) {
        requests.push(
          fetchJson(`/connect/tournament-streams/${encodeURIComponent(tournamentId)}/me`, {
            auth: true,
          })
        );
      }

      const results = await Promise.allSettled(requests);
      const tournamentResult = results[0];
      const sourcesResult = results[1];
      const mineResult = userSignedIn ? results[2] : null;

      if (tournamentResult.status === "fulfilled") {
        setTournament(tournamentResult.value?.tournament || null);
      } else if (!silent) {
        setError(
          tournamentResult.reason?.message || "The Tournament Live Hub could not load right now."
        );
      }

      if (sourcesResult.status === "fulfilled") {
        setSources(Array.isArray(sourcesResult.value?.sources) ? sourcesResult.value.sources : []);
      }

      if (mineResult?.status === "fulfilled") {
        setMySetup(mineResult.value || null);
        const existing = mineResult.value?.source;
        if (existing?.twitchChannel) setTwitchChannel(existing.twitchChannel);
        if (existing?.platform) setPlatform(existing.platform);
      } else if (userSignedIn) {
        setMySetup(null);
      }

      if (!silent) setLoading(false);
    },
    [tournamentId, userSignedIn]
  );

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load({ silent: true }), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  const matches = useMemo(
    () => (Array.isArray(tournament?.matches) ? tournament.matches : []),
    [tournament?.matches]
  );

  const myCurrentMatch = useMemo(
    () => currentMatchForTeam(matches, mySetup?.team?.id),
    [matches, mySetup?.team?.id]
  );

  const feeds = useMemo(() => {
    return (Array.isArray(sources) ? sources : [])
      .map((source) => {
        if (source?.enabled === false || !source?.teamId || !source?.twitchChannel) return null;
        const match = currentMatchForTeam(matches, source.teamId);
        if (!match?.id) return null;
        return {
          ...source,
          matchId: match.id,
          match,
          displayName: source.gaUsername || source.teamName || "Tournament Participant",
          matchLabel: matchLabel(match, source.teamName || "Tournament Game"),
          roundLabel: roundLabel(match),
          sourceLabel: source.platform === "xbox" ? "XBOX DIRECT" : "PS5 DIRECT",
        };
      })
      .filter(Boolean);
  }, [matches, sources]);

  useEffect(() => {
    if (!feeds.length) {
      setSelectedId("");
      return;
    }
    if (!feeds.some((feed) => feed.id === selectedId)) {
      setSelectedId(feeds[0].id);
    }
  }, [feeds, selectedId]);

  const selectedIndex = Math.max(0, feeds.findIndex((feed) => feed.id === selectedId));
  const selected = feeds[selectedIndex] || feeds[0] || null;
  const chatRoom = `tournament:${String(tournament?.id || tournamentId)}`;
  const isCrewHq = String(tournament?.id || tournamentId) === CREW_HQ_ID;
  const bracketPath = isCrewHq ? `/tournaments/${CREW_HQ_SLUG}` : "/tournaments";
  const sharing = Boolean(mySetup?.source && mySetup.source.enabled !== false);

  const setSelectedStatus = useCallback(
    (status) => {
      if (!selected?.id) return;
      setStatuses((current) => ({ ...current, [selected.id]: status }));
    },
    [selected?.id]
  );

  function selectFeed(id) {
    setSelectedId(id);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("feed", id);
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    } catch {
      // URL sync is optional.
    }
  }

  function rotate(direction) {
    if (feeds.length < 2) return;
    const next = (selectedIndex + direction + feeds.length) % feeds.length;
    selectFeed(feeds[next].id);
  }

  async function saveStreamSetup(event) {
    event?.preventDefault?.();
    if (!myCurrentMatch?.id) {
      setSetupNotice("Your team does not have an active matchup ready to stream yet.");
      return;
    }
    if (!String(twitchChannel || "").trim()) {
      setSetupNotice("Enter the Twitch username linked to the console you will broadcast from.");
      return;
    }

    setSetupBusy(true);
    setSetupNotice("");
    try {
      const result = await fetchJson(
        `/connect/tournament-streams/${encodeURIComponent(tournamentId)}/opt-in`,
        {
          auth: true,
          method: "POST",
          body: {
            matchId: myCurrentMatch.id,
            twitchChannel: twitchChannel.trim(),
            platform,
            enabled: true,
          },
        }
      );
      setMySetup((current) => ({ ...(current || {}), source: result?.source || null }));
      setSetupNotice(
        `Attached to ${matchLabel(myCurrentMatch)}. Start your ${
          platform === "xbox" ? "Xbox" : "PS5"
        } broadcast through Twitch and this game can appear in the Tournament Live Hub.`
      );
      await load({ silent: true });
    } catch (saveError) {
      setSetupNotice(saveError?.message || "Could not attach your tournament stream.");
    } finally {
      setSetupBusy(false);
    }
  }

  async function stopSharing() {
    if (!mySetup?.source) return;
    setSetupBusy(true);
    setSetupNotice("");
    try {
      await fetchJson(`/connect/tournament-streams/${encodeURIComponent(tournamentId)}/opt-in`, {
        auth: true,
        method: "POST",
        body: {
          matchId: myCurrentMatch?.id || mySetup.source.matchId,
          twitchChannel: mySetup.source.twitchChannel || twitchChannel,
          platform: mySetup.source.platform || platform,
          enabled: false,
        },
      });
      setSetupNotice("Your tournament broadcast has been removed from this tournament hub.");
      await load({ silent: true });
    } catch (stopError) {
      setSetupNotice(stopError?.message || "Could not remove your tournament stream.");
    } finally {
      setSetupBusy(false);
    }
  }

  if (loading) {
    return (
      <main className="ga-th-page">
        <style>{LIVE_HUB_CSS}</style>
        <div className="ga-th-card ga-th-loading">Loading Tournament Live Hub…</div>
      </main>
    );
  }

  return (
    <main className="ga-th-page">
      <style>{LIVE_HUB_CSS}</style>

      <section className="ga-th-hero">
        <div className="ga-th-hero-row">
          <div>
            <div className="ga-th-kicker">GASTREAMING • TOURNAMENT LIVE CONTROL ROOM</div>
            <h1>{isCrewHq ? "G.A.S UP Live Hub" : "Tournament Live Hub"}</h1>
            <p>
              Participant games only. Switch between simultaneous tournament broadcasts while everybody stays together in one tournament-wide chat.
            </p>
          </div>
          <div className="ga-th-actions">
            <Link className="ga-th-link" to={bracketPath}>← BRACKET / EVENT</Link>
            {mySetup?.eligible ? (
              <a className="ga-th-link primary" href="#stream-this-match">STREAM THIS MATCH</a>
            ) : null}
          </div>
        </div>
      </section>

      {error ? <div className="ga-th-error">{error}</div> : null}

      <section className="ga-th-grid">
        <article className="ga-th-card ga-th-player">
          {selected ? (
            <>
              <div className="ga-th-meta">
                <div>
                  <div className="ga-th-round">{selected.roundLabel}</div>
                  <div className="ga-th-title">{selected.matchLabel}</div>
                  <div className="ga-th-sub">Tournament feed from {selected.displayName}</div>
                </div>
                <div className="ga-th-badges">
                  <span className={`ga-th-badge ${sourceStatus(selected, statuses) === "LIVE" ? "live" : ""}`}>
                    {sourceStatus(selected, statuses)}
                  </span>
                  <span className="ga-th-badge">{selected.sourceLabel}</span>
                </div>
              </div>

              <div className="ga-th-stage">
                <TwitchHubPlayer source={selected} onStatus={setSelectedStatus} />
                <button className="ga-th-arrow left" type="button" onClick={() => rotate(-1)} disabled={feeds.length < 2} aria-label="Previous tournament game">‹</button>
                <button className="ga-th-arrow right" type="button" onClick={() => rotate(1)} disabled={feeds.length < 2} aria-label="Next tournament game">›</button>
              </div>

              <div className="ga-th-bottom">
                <div className="ga-th-count">GAME FEED {selectedIndex + 1} OF {feeds.length}</div>
                <div className="ga-th-actions">
                  {selected.gaUsername ? (
                    <span className="ga-th-identity">GA BROADCASTER • @{selected.gaUsername}</span>
                  ) : null}
                </div>
              </div>
            </>
          ) : (
            <div className="ga-th-empty">
              <strong>No tournament game is broadcasting yet.</strong>
              <span>When any accepted tournament participant attaches their PS5 or Xbox broadcast, that player's feed appears here automatically.</span>
            </div>
          )}
        </article>

        <aside className="ga-th-card ga-th-chat">
          <div className="ga-th-chat-head">
            <div className="ga-th-kicker">ONE ROOM • EVERY GAME</div>
            <h2>Tournament Chat</h2>
            <p>Changing the video feed never changes this room.</p>
          </div>
          <div className="ga-th-chat-body">
            <ChatBox room={chatRoom} title="TOURNAMENT CHAT" />
          </div>
        </aside>
      </section>

      <section className="ga-th-card ga-th-strip">
        <div className="ga-th-strip-head">
          <h2>Live Game Selector</h2>
          <div className="ga-th-count">{feeds.length} ACTIVE TOURNAMENT FEED{feeds.length === 1 ? "" : "S"}</div>
        </div>
        {feeds.length ? (
          <div className="ga-th-feeds">
            {feeds.map((feed) => (
              <button key={feed.id} type="button" className={`ga-th-feed ${feed.id === selected?.id ? "active" : ""}`} onClick={() => selectFeed(feed.id)}>
                <div className="ga-th-feed-top">
                  <span className="ga-th-feed-name">{feed.matchLabel}</span>
                  <span className="ga-th-feed-status">{sourceStatus(feed, statuses)}</span>
                </div>
                <div className="ga-th-feed-match">{feed.roundLabel} • {feed.displayName} • {feed.sourceLabel}</div>
              </button>
            ))}
          </div>
        ) : (
          <div className="ga-th-count">No participant feeds are attached to an active matchup right now.</div>
        )}
      </section>

      {userSignedIn && mySetup?.eligible ? (
        <section className="ga-th-card ga-th-setup" id="stream-this-match">
          <div className="ga-th-kicker">PARTICIPANT • OPTIONAL TOURNAMENT BROADCAST</div>
          <h2>Stream This Match</h2>
          <p>
            Every accepted player on the roster can attach their own tournament feed. Your GAStreaming identity stays your GA username/team; Twitch is only the console broadcast source.
          </p>

          <div className="ga-th-current">
            <strong>{mySetup?.team?.name || "Your team"}</strong>
            <span> • {myCurrentMatch ? matchLabel(myCurrentMatch) : "Waiting for the next matchup"}</span>
            {sharing ? <span> • Attached: @{mySetup.source.twitchChannel}</span> : null}
          </div>

          <form className="ga-th-form" onSubmit={saveStreamSetup}>
            <label className="ga-th-field">
              Twitch username linked to your console
              <input className="ga-th-input" value={twitchChannel} onChange={(event) => setTwitchChannel(event.target.value)} placeholder="Twitch username" autoComplete="off" />
            </label>
            <label className="ga-th-field">
              Console
              <select className="ga-th-select" value={platform} onChange={(event) => setPlatform(event.target.value)}>
                <option value="ps5">PlayStation 5</option>
                <option value="xbox">Xbox Series X|S</option>
              </select>
            </label>
            <button className="ga-th-btn primary" type="submit" disabled={setupBusy || !myCurrentMatch?.id}>
              {setupBusy ? "SAVING…" : sharing ? "ATTACH CURRENT MATCH" : "STREAM THIS MATCH"}
            </button>
          </form>

          {sharing ? (
            <button className="ga-th-btn ga-th-stop" type="button" disabled={setupBusy} onClick={stopSharing}>
              STOP SHARING MY TOURNAMENT STREAM
            </button>
          ) : null}

          {setupNotice ? <div className="ga-th-notice">{setupNotice}</div> : null}
          {!myCurrentMatch ? (
            <div className="ga-th-notice">This control unlocks as soon as your bracket matchup has both teams filled in.</div>
          ) : null}
        </section>
      ) : userSignedIn && mySetup && !mySetup?.eligible ? (
        <section className="ga-th-card ga-th-setup">
          <div className="ga-th-kicker">TOURNAMENT STREAMING</div>
          <h2>Registered participants can stream here.</h2>
          <p>Once you are an accepted member of a tournament roster and your matchup is active, you can attach your own PS5 or Xbox Twitch broadcast.</p>
        </section>
      ) : null}
    </main>
  );
}

const LIVE_HUB_CSS = `
.ga-th-page{width:min(1500px,calc(100% - 22px));margin:0 auto 70px;color:#fff}.ga-th-page *{box-sizing:border-box}.ga-th-card,.ga-th-hero{border:1px solid rgba(255,255,255,.1);background:linear-gradient(145deg,rgba(33,7,49,.97),rgba(5,5,10,.98));box-shadow:0 24px 70px rgba(0,0,0,.34)}.ga-th-hero{border-radius:24px;padding:22px 24px;margin-bottom:14px;background:radial-gradient(circle at 86% 0,rgba(124,56,255,.28),transparent 36%),radial-gradient(circle at 15% 15%,rgba(0,182,255,.16),transparent 34%),linear-gradient(145deg,rgba(26,6,45,.98),rgba(5,5,9,.98))}.ga-th-kicker{font-size:10px;font-weight:1000;letter-spacing:.16em;color:#cda8ff}.ga-th-hero-row{display:flex;justify-content:space-between;gap:18px;align-items:flex-end;flex-wrap:wrap}.ga-th-hero h1{font-size:clamp(2rem,5vw,4.5rem);line-height:.95;margin:5px 0}.ga-th-hero p{max-width:850px;color:rgba(255,255,255,.65);margin:0;line-height:1.5}.ga-th-actions{display:flex;gap:8px;flex-wrap:wrap}.ga-th-link,.ga-th-btn{display:inline-flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.15);border-radius:12px;background:rgba(255,255,255,.055);color:#fff;text-decoration:none;padding:10px 13px;font-weight:900;cursor:pointer}.ga-th-link.primary,.ga-th-btn.primary{background:linear-gradient(90deg,#7137ff,#008fca);border-color:rgba(0,200,255,.5)}.ga-th-grid{display:grid;grid-template-columns:minmax(0,1.6fr) minmax(320px,.55fr);gap:14px}.ga-th-card{border-radius:20px;overflow:hidden}.ga-th-player{padding:14px}.ga-th-meta{display:flex;justify-content:space-between;gap:12px;align-items:center;flex-wrap:wrap;margin-bottom:12px}.ga-th-round{color:#b991ff;font-size:10px;font-weight:1000;letter-spacing:.12em}.ga-th-title{font-size:clamp(1.1rem,2.5vw,1.8rem);font-weight:1000}.ga-th-sub{margin-top:4px;color:rgba(255,255,255,.52);font-size:12px}.ga-th-badges{display:flex;gap:7px}.ga-th-badge{font-size:9px;font-weight:1000;letter-spacing:.08em;padding:6px 9px;border-radius:999px;background:rgba(255,255,255,.07)}.ga-th-badge.live{background:rgba(0,175,105,.28);color:#baffda;border:1px solid rgba(60,255,170,.28)}.ga-th-stage{position:relative;aspect-ratio:16/9;background:#000;border-radius:15px;overflow:hidden}.ga-th-twitch-host,.ga-th-twitch-host>div,.ga-th-twitch-host iframe{width:100%!important;height:100%!important;display:block!important;background:#000}.ga-th-arrow{position:absolute;z-index:5;top:50%;transform:translateY(-50%);width:52px;height:74px;border-radius:15px;border:1px solid rgba(255,255,255,.26);background:rgba(0,0,0,.72);color:#fff;font-size:34px;cursor:pointer}.ga-th-arrow.left{left:12px}.ga-th-arrow.right{right:12px}.ga-th-arrow:disabled{display:none}.ga-th-bottom{display:flex;justify-content:space-between;gap:12px;align-items:center;flex-wrap:wrap;margin-top:11px}.ga-th-count{font-size:11px;font-weight:900;color:rgba(255,255,255,.5)}.ga-th-identity{display:inline-flex;align-items:center;padding:8px 11px;border-radius:999px;border:1px solid rgba(185,145,255,.22);background:rgba(106,53,178,.12);color:#d9c7ff;font-size:10px;font-weight:1000;letter-spacing:.06em}.ga-th-empty{min-height:420px;display:flex;flex-direction:column;gap:8px;align-items:center;justify-content:center;text-align:center;padding:35px;color:rgba(255,255,255,.6);line-height:1.55}.ga-th-empty strong{font-size:20px;color:#fff}.ga-th-chat{min-height:660px}.ga-th-chat-head{padding:15px 16px 6px}.ga-th-chat-head h2{margin:4px 0 3px}.ga-th-chat-head p{margin:0;color:rgba(255,255,255,.5);font-size:11px;line-height:1.45}.ga-th-chat-body{height:590px;min-height:0}.ga-th-chat-body>div{height:100%}.ga-th-strip,.ga-th-setup{margin-top:14px;padding:14px}.ga-th-strip-head{display:flex;justify-content:space-between;gap:12px;align-items:center;margin-bottom:11px}.ga-th-strip-head h2{margin:0;font-size:1.15rem}.ga-th-feeds{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:9px}.ga-th-feed{border:1px solid rgba(255,255,255,.09);background:rgba(255,255,255,.035);border-radius:14px;padding:13px;text-align:left;color:#fff;cursor:pointer}.ga-th-feed.active{border-color:rgba(109,77,255,.65);background:rgba(88,36,170,.2)}.ga-th-feed-top{display:flex;justify-content:space-between;gap:8px;align-items:center}.ga-th-feed-name{font-weight:1000}.ga-th-feed-status{font-size:9px;font-weight:1000;letter-spacing:.08em;color:#b9ddff}.ga-th-feed-match{margin-top:7px;color:rgba(255,255,255,.54);font-size:11px}.ga-th-setup h2{margin:5px 0 7px}.ga-th-setup p{color:rgba(255,255,255,.62);line-height:1.5;margin:0 0 12px}.ga-th-form{display:grid;grid-template-columns:1fr 180px auto;gap:9px;align-items:end}.ga-th-field{display:grid;gap:5px;font-size:11px;font-weight:900;color:rgba(255,255,255,.72)}.ga-th-input,.ga-th-select{width:100%;background:#0b0b0e;color:#fff;border:1px solid rgba(255,255,255,.15);border-radius:11px;padding:11px 12px;outline:none}.ga-th-current{margin:10px 0;padding:11px 12px;border-radius:12px;background:rgba(255,255,255,.045);font-size:12px;color:rgba(255,255,255,.75)}.ga-th-notice{margin-top:10px;padding:10px 12px;border-radius:11px;background:rgba(96,48,160,.26);color:#eadcff;font-size:12px}.ga-th-error{margin-bottom:12px;padding:11px 13px;border-radius:12px;background:rgba(120,10,18,.35);border:1px solid rgba(255,73,84,.25);color:#ffb7bd}.ga-th-loading{padding:30px}.ga-th-stop{margin-top:10px;background:transparent}@media(max-width:950px){.ga-th-grid{grid-template-columns:1fr}.ga-th-chat{min-height:540px}.ga-th-chat-body{height:480px}.ga-th-form{grid-template-columns:1fr 1fr}.ga-th-form .ga-th-btn{grid-column:1/-1}}@media(max-width:620px){.ga-th-page{width:min(100% - 12px,1500px)}.ga-th-hero{padding:18px 16px}.ga-th-player{padding:8px}.ga-th-arrow{width:42px;height:62px;font-size:27px}.ga-th-arrow.left{left:6px}.ga-th-arrow.right{right:6px}.ga-th-feeds,.ga-th-form{grid-template-columns:1fr}.ga-th-chat-body{height:430px}}
`;
